/**
 * "Válassz napot" — the month view behind the week strip's calendar button:
 * the app's existing react-datetime calendar, inline, in the portal's bottom
 * sheet. Picking a day hands back its 'YYYY-MM-DD' and closes.
 *
 * Days are Budapest calendar dates throughout, like the week strip's:
 * react-datetime only ever handles the calendar date here, and its own
 * "today" (the DEVICE's today — wrong on a phone set to another timezone)
 * is replaced by the `today` the caller gets. Days with work carry the
 * week strip's dot, from `tripCounts`; a caller with no such counts to hand
 * passes none and gets plain days.
 *
 * ONE SHEET, TWO LANGUAGES. `copy` names everything, including the moment
 * locale the month is written in: the driver's Hungarian by default
 * (portalFormat's DAY_PICKER_COPY), the office's English on the dispatch
 * board (operatorFormat's DAY_PICKER_COPY_EN). Never a fork — the dispatcher
 * opens the very picker the driver does.
 */
import React from 'react';
import moment from 'moment';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import BottomSheet from './BottomSheet';
import { DAY_PICKER_COPY } from '../portalFormat';

// react-datetime's `locale="hu"` needs moment's Hungarian locale registered,
// and registering a locale always makes it moment's GLOBAL one too (moment's
// defineLocale; it has no side-effect-free way). So load it the way moment's
// own lazy loader does: note the global locale, load, put it back. That runs
// synchronously while this module is evaluated — PortalRoutes imports it
// eagerly — so no other code, the admin app included, ever runs with
// Hungarian as moment's locale, whatever locale it had chosen. A static
// `import` could not do this: it would run before the line that notes it.
const globalLocale = moment.locale();
// eslint-disable-next-line global-require
require('moment/locale/hu');

moment.locale(globalLocale);

export default function DayPickerSheet({
  selected, today, tripCounts = {}, copy = DAY_PICKER_COPY, onPick, onClose,
}) {
  // react-datetime's cell, with its "today" judged in Budapest and a dot
  // under a day with trips.
  const renderDay = (props, day) => {
    const date = day.format('YYYY-MM-DD');
    const trips = tripCounts[date] || 0;
    const classes = props.className.split(' ').filter((name) => name !== 'rdtToday');
    if (date === today) classes.push('rdtToday');
    if (trips) classes.push('pp-month__busy');
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <td {...props} className={classes.join(' ')}>
        {day.date()}
        {trips ? <span className="visually-hidden">{copy.trips(trips)}</span> : null}
      </td>
    );
  };

  return (
    <BottomSheet title={copy.title} closeLabel={copy.closeLabel} onClose={onClose}>
      <div className="pp-month">
        <Datetime
          input={false}
          open
          locale={copy.locale}
          timeFormat={false}
          renderDay={renderDay}
          value={moment(selected, 'YYYY-MM-DD')}
          onChange={(value) => {
            // A typed or half-picked value arrives as a string; only a real day counts.
            if (moment.isMoment(value)) onPick(value.format('YYYY-MM-DD'));
          }}
        />
      </div>
    </BottomSheet>
  );
}
